/**
 * AccountPlanTab.tsx
 *
 * Full account plan view — mounted as the "Plan" tab inside AccountScorecard.
 *
 * Sections (vertical scroll desktop, accordion mobile):
 *  1. Header — account name, industry, composite score, days since last touch
 *  2. Bootcamp foundation — why_this_account, initial_pain_hypothesis, source_of_awareness
 *  3. Relationship map — contacts with role, owner type, warmth, last touch
 *  4. M-PACT discovery state — CP1–CP10 table with inline answer modal
 *  5. Opportunities — list with OpportunityPlanCard, "New opportunity" button
 *  6. Next best play — auto-suggested play with mark done / override
 *  7. Disqualification banner — stall warning
 *  8. Audit log — last 20 changes, collapsed by default
 *
 * Persistence: localStorage key aip:account-plan:{account_id}
 * Hydrated from TOP10_BOOTCAMP_SEED if no localStorage entry exists.
 *
 * Brand: Navy #0B0B0F · Red #DC2626 · AIP bg #FAFAF7
 * Roles: bdr, ae, manager, executive, marketing — NEVER "rep"
 * M-PACT always spelled with hyphen
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "wouter";
import { supabase, getSession } from "@/lib/session";
import {
  ChevronDown, ChevronUp, Plus, Edit2, Save, X, Star, AlertTriangle,
  CheckCircle2, RefreshCw, User,
} from "lucide-react";
import type {
  AccountPlan, RelationshipNode, Opportunity, AuditEntry, CheckpointField,
} from "@/types/account-plan";
import { blankCheckpoint, blankMpact, defaultDisqualification } from "@/types/account-plan";
import {
  loadPlan, savePlan, blankPlan, daysSinceActivity, relTime, appendAudit,
} from "@/lib/account-plan-store";
import { OpportunityPlanCard } from "./OpportunityPlanCard";
import { getHealthColor } from "@shared/schema";

// Lazy seed loader — returns the seed map if available, empty object otherwise.
async function loadSeed(): Promise<Record<string, AccountPlan>> {
  try {
    const mod = await import("@/data/top10-bootcamp-seed");
    return (mod as any).TOP10_BOOTCAMP_SEED ?? {};
  } catch {
    return {};
  }
}

// ─── Brand constants ───────────────────────────────────────────────────────────
const BLACK = "#0B0B0F";
const RED   = "#DC2626";
const BG    = "#FAFAF7";

// ─── CP1–CP10 metadata ────────────────────────────────────────────────────────
// D-091 (2026-05-22): Display labels realigned to canon (HELIX_CHECKPOINTS in
// lib/helix-checkpoints.ts). Storage `key` names are intentionally left as the
// legacy slugs (cp2_pain, cp3_demonstration, etc.) because account_plan_mpact
// rows already use those keys — renaming would orphan historical answers.
// Canon order: Project, Need, Pain, Fit, Budget, Timing, Authority, Org, Competition, Final Barrier.
const CP_META = [
  { key: "cp1_project" as const,       cp: 1,  label: "Project",                      question: "Is there a real, funded project? New logo or expansion opportunity confirmed?" },
  { key: "cp2_pain" as const,          cp: 2,  label: "Need / Pain / Demonstration",  question: "Does the customer have a recognized need, demonstrable pain, or provable business case we can address?" },
  { key: "cp3_demonstration" as const, cp: 3,  label: "Budget",                       question: "Is there real money, a named source, and a validated line item?" },
  { key: "cp4_budget" as const,        cp: 4,  label: "Timeline",                     question: "Is there a decision window AND an implementation window — both real?" },
  { key: "cp5_timeline" as const,      cp: 5,  label: "Authority — Project / Initiative", question: "Do we have direct access to the project or initiative authority who can champion and sponsor this deal?" },
  { key: "cp6_authority" as const,     cp: 6,  label: "Technical and Financial Sign-off", question: "Have technical evaluation and financial approval gates been identified and engaged?" },
  { key: "cp7_people" as const,        cp: 7,  label: "Org Structure and Influence Map", question: "Do we understand org structure and the full influence map — including where formal authority and real power diverge?" },
  { key: "cp8_competition" as const,   cp: 8,  label: "Competitive Position",         question: "Do we understand the competitive landscape and where we stand in it?" },
  { key: "cp9_finance" as const,       cp: 9,  label: "Compliance — IT Internal and Industry-Specific", question: "Do we understand every IT internal and industry-specific compliance requirement that could block or delay the deal?" },
  { key: "cp10_legal" as const,        cp: 10, label: "Legal — Contracts, SLAs, Terms", question: "Do we understand every barrier to signature — and have a path through each?" },
] as const;

type CpKey = typeof CP_META[number]["key"];

// ─── Owner type display ────────────────────────────────────────────────────────
const OWNER_LABELS: Record<string, string> = {
  champion: "Champion",
  economic_buyer: "Economic",
  user_buyer: "User",
  technical_buyer: "Technical",
  fox: "Project Lead",
  detractor: "Detractor",
  blocker: "Blocker",
  coach: "Coach",
  unknown: "Unknown",
};

const OWNER_COLOR: Record<string, string> = {
  champion: "#16A34A",
  economic_buyer: "#0B0B0F",
  user_buyer: "#525258",
  technical_buyer: "#DC2626",
  fox: "#EA580C",
  detractor: "#DC2626",
  blocker: "#DC2626",
  coach: "#DC2626",
  unknown: "#8E8E94",
};

function warmthStars(warmth: number) {
  return Array.from({ length: 5 }, (_, i) => (
    <Star
      key={i}
      className="w-3 h-3 inline"
      style={{ fill: i < warmth ? "#EAB308" : "none", color: i < warmth ? "#EAB308" : "#C9C9CC" }}
    />
  ));
}

// ─── Section wrapper with accordion on mobile ─────────────────────────────────
function Section({
  title, children, defaultOpen = true,
  badge,
}: {
  title: string; children: React.ReactNode; defaultOpen?: boolean; badge?: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden mb-4">
      <button
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-neutral-50 transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: BLACK }}>
            {title}
          </span>
          {badge}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-neutral-500" /> : <ChevronDown className="w-4 h-4 text-neutral-500" />}
      </button>
      {open && <div className="px-5 pb-5 pt-1">{children}</div>}
    </div>
  );
}

// ─── Inline field editor ───────────────────────────────────────────────────────
function EditableText({
  label, value, onSave, multiline = false,
}: { label: string; value: string; onSave: (v: string) => void; multiline?: boolean }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (editing) {
    return (
      <div>
        <label className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</label>
        {multiline ? (
          <textarea
            autoFocus
            className="w-full mt-1 px-3 py-2 rounded border border-neutral-200 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-neutral-400"
            rows={3}
            value={draft}
            onChange={e => setDraft(e.target.value)}
          />
        ) : (
          <input
            autoFocus
            className="w-full mt-1 px-3 py-2 rounded border border-neutral-200 text-sm focus:outline-none focus:ring-1 focus:ring-neutral-400"
            value={draft}
            onChange={e => setDraft(e.target.value)}
          />
        )}
        <div className="flex gap-2 mt-2">
          <button
            onClick={() => { onSave(draft); setEditing(false); }}
            className="inline-flex items-center gap-1 px-3 py-1 rounded text-xs font-medium"
            style={{ background: BLACK, color: "#fff" }}
          >
            <Save className="w-3 h-3" /> Save
          </button>
          <button
            onClick={() => { setDraft(value); setEditing(false); }}
            className="inline-flex items-center gap-1 px-3 py-1 rounded text-xs font-medium border border-neutral-200 text-neutral-600"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group relative">
      <label className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</label>
      <p className="text-sm mt-0.5 text-neutral-700 leading-relaxed min-h-[1.5rem]">
        {value && value.trim() !== "" && value.trim().toLowerCase() !== "not yet filled in"
          ? value
          : <span className="text-neutral-400 italic">Click the pencil to add notes</span>}
      </p>
      <button
        onClick={() => setEditing(true)}
        className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded text-neutral-500 hover:text-neutral-600"
      >
        <Edit2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ─── M-PACT answer modal ───────────────────────────────────────────────────────
function MpactAnswerModal({
  cp,
  question,
  label,
  field,
  onSave,
  onClose,
}: {
  cp: number;
  question: string;
  label: string;
  field: CheckpointField;
  onSave: (answer: string, notes: string) => void;
  onClose: () => void;
}) {
  const [answer, setAnswer] = useState(field.answer);
  const [notes, setNotes] = useState(field.notes);

  // Esc key dismissal
  useEffect(() => {
    function handleEsc(e: KeyboardEvent) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      style={{ background: "rgba(15,23,42,0.5)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="rounded-xl p-6 w-full max-w-lg mx-4"
        style={{ background: "#fff" }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div
              className="text-[10px] font-semibold uppercase tracking-wider mb-1"
              style={{ color: RED }}
            >
              CP{cp} · {label}
            </div>
            <p className="text-sm font-medium" style={{ color: BLACK }}>{question}</p>
          </div>
          <button onClick={onClose} className="ml-4 text-neutral-500 hover:text-neutral-600">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-neutral-500">Answer</label>
            <textarea
              autoFocus
              className="w-full mt-1 px-3 py-2 rounded border border-neutral-200 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-neutral-400"
              rows={4}
              placeholder="Enter the answer based on what the prospect confirmed..."
              value={answer}
              onChange={e => setAnswer(e.target.value)}
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-neutral-500">Session notes (optional)</label>
            <textarea
              className="w-full mt-1 px-3 py-2 rounded border border-neutral-200 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-neutral-400"
              rows={2}
              placeholder="Context for this answer — call date, who confirmed, etc."
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-3 mt-5">
          <button
            onClick={() => onSave(answer, notes)}
            className="flex-1 py-2 rounded text-sm font-medium"
            style={{ background: BLACK, color: "#fff" }}
          >
            Save to audit log
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-2 rounded text-sm font-medium border border-neutral-200 text-neutral-600"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Add Contact modal ─────────────────────────────────────────────────────────
function AddContactModal({
  onAdd,
  onClose,
}: {
  onAdd: (node: RelationshipNode) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<RelationshipNode>({
    name: "", role: "", org: "", owner: "unknown",
    warmth: 3, last_touch: "", notes: "",
  });

  function set<K extends keyof RelationshipNode>(k: K, v: RelationshipNode[K]) {
    setForm(f => ({ ...f, [k]: v }));
  }

  // Esc key dismissal
  useEffect(() => {
    function handleEsc(e: KeyboardEvent) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      style={{ background: "rgba(15,23,42,0.5)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="rounded-xl p-6 w-full max-w-lg mx-4"
        style={{ background: "#fff" }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold" style={{ color: BLACK }}>Add contact</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-neutral-500" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {([
            ["name", "Name", "text"],
            ["role", "Title / role", "text"],
            ["org", "Organisation", "text"],
            ["last_touch", "Last touch", "date"],
          ] as [keyof RelationshipNode, string, string][]).map(([k, l, t]) => (
            <div key={String(k)}>
              <label className="text-[11px] uppercase tracking-wider text-neutral-500">{l}</label>
              <input
                type={t}
                className="w-full mt-1 px-3 py-1.5 rounded border border-neutral-200 text-sm focus:outline-none focus:ring-1 focus:ring-neutral-400"
                value={String(form[k])}
                onChange={e => set(k, e.target.value as any)}
              />
            </div>
          ))}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-neutral-500">Owner type</label>
            <select
              className="w-full mt-1 px-3 py-1.5 rounded border border-neutral-200 text-sm focus:outline-none"
              value={form.owner}
              onChange={e => set("owner", e.target.value as RelationshipNode["owner"])}
            >
              {Object.entries(OWNER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-neutral-500">Warmth (1–5)</label>
            <input
              type="number" min={1} max={5}
              className="w-full mt-1 px-3 py-1.5 rounded border border-neutral-200 text-sm focus:outline-none"
              value={form.warmth}
              onChange={e => set("warmth", Number(e.target.value) as RelationshipNode["warmth"])}
            />
          </div>
        </div>
        <div className="mt-3">
          <label className="text-[11px] uppercase tracking-wider text-neutral-500">Notes</label>
          <textarea
            className="w-full mt-1 px-3 py-1.5 rounded border border-neutral-200 text-sm resize-none focus:outline-none"
            rows={2}
            value={form.notes}
            onChange={e => set("notes", e.target.value)}
          />
        </div>
        <div className="flex gap-3 mt-4">
          <button
            onClick={() => { if (form.name.trim()) { onAdd(form); onClose(); } }}
            className="flex-1 py-2 rounded text-sm font-medium"
            style={{ background: BLACK, color: "#fff" }}
          >
            Add contact
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-2 rounded text-sm font-medium border border-neutral-200 text-neutral-600"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── New Opportunity blank factory ─────────────────────────────────────────────
function blankOpportunity(accountName: string): Opportunity {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    name: `${accountName} — New opportunity`,
    stage: "cp1_project",
    expected_close: "",
    amount: 0,
    win_probability: 10,
    forecast_bucket: "OMITTED",
    next_step: "",
    next_step_date: "",
    competition: "",
    decision_criteria: [],
    blockers: [],
    composite_score: 0,
    created_by: "ae",
    created_at: now,
    updated_at: now,
  };
}

// ─── Main component ────────────────────────────────────────────────────────────

interface Props {
  accountId: string;
  accountName: string;
  industry: string;
  compositeScore: number;
}

export function AccountPlanTab({ accountId, accountName, industry, compositeScore }: Props) {
  const [plan, setPlanState] = useState<AccountPlan | null>(null);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [mpactModal, setMpactModal] = useState<CpKey | null>(null);
  const [showAddContact, setShowAddContact] = useState(false);
  const [showBootcampEdit, setShowBootcampEdit] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [newOppId, setNewOppId] = useState<string | null>(null);

  // Question-level answered count, read from the same localStorage key the
  // Opportunity DNA tab uses (aip:helix:<accountId>). This keeps the Plan tab
  // badge honest when discovery is in progress but no CP-level synthesis has
  // been written yet (e.g. DNA shows 36/104 questions, Plan would otherwise
  // show 0/10 CPs and look broken). Refreshed on focus and storage events.
  const [helixAnsweredQs, setHelixAnsweredQs] = useState<{ answered: number; total: number }>({ answered: 0, total: 0 });
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const [{ ALL_QUESTIONS }] = await Promise.all([import("@shared/questions")]);
        const total = ALL_QUESTIONS.length;
        let answered = 0;
        let needsDbHydration = true;
        try {
          const raw = localStorage.getItem(`aip:helix:${accountId}`);
          if (raw) {
            const store = JSON.parse(raw) as Record<string, { selectedOption?: number | null } | null>;
            answered = ALL_QUESTIONS.filter((q: { key: string }) => {
              const opt = store?.[q.key]?.selectedOption;
              return opt !== null && opt !== undefined;
            }).length;
            if (Object.keys(store ?? {}).length > 0) needsDbHydration = false;
          }
        } catch { /* localStorage unavailable */ }

        // DB hydration: when localStorage is empty for this account, fall back
        // to account_answers in Supabase so the badge shows real progress
        // (e.g. Pattern Energy has 55 answers in DB but a fresh browser session
        // would otherwise show 0/190). Mirrors the OperationalHelix hydration
        // pattern and seeds localStorage for fast subsequent loads.
        if (needsDbHydration) {
          const accountIdNum = parseInt(accountId, 10);
          if (!Number.isNaN(accountIdNum)) {
            const { data, error } = await supabase()
              .from("account_answers")
              .select("question_key, selected_option, answer_notes, updated_at")
              .eq("account_id", accountIdNum);
            if (!error && data && data.length > 0) {
              const hydrated: Record<string, { selectedOption: string | null; notes: string; updatedAt: string; updatedBy: string }> = {};
              for (const row of data) {
                if (!row.question_key) continue;
                hydrated[row.question_key] = {
                  selectedOption: row.selected_option ?? null,
                  notes: row.answer_notes ?? "",
                  updatedAt: row.updated_at ?? new Date().toISOString(),
                  updatedBy: "db-hydrated",
                };
              }
              answered = ALL_QUESTIONS.filter((q: { key: string }) => {
                const opt = hydrated[q.key]?.selectedOption;
                return opt !== null && opt !== undefined;
              }).length;
              try { localStorage.setItem(`aip:helix:${accountId}`, JSON.stringify(hydrated)); } catch { /* quota */ }
            }
          }
        }

        if (!cancelled) setHelixAnsweredQs({ answered, total });
      } catch { /* questions module unavailable */ }
    };
    refresh();
    const onStorage = (e: StorageEvent) => {
      if (e.key === `aip:helix:${accountId}`) refresh();
    };
    const onFocus = () => refresh();
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFocus);
    };
  }, [accountId]);

  // Load / hydrate plan
  useEffect(() => {
    let cancelled = false;
    async function init() {
      let p = await loadPlan(accountId);
      if (!p) {
        // Try to hydrate from Top 10 seed
        const seedMap = await loadSeed();
        const seed = seedMap[accountId];
        if (seed) {
          p = seed;
          await savePlan(p);
        } else {
          p = blankPlan(accountId, accountName, industry);
          await savePlan(p);
        }
      }
      if (!cancelled) {
        setPlanState(p);
        setLoading(false);
      }
    }
    init();
    return () => { cancelled = true; };
  }, [accountId, accountName, industry]);

  const setPlan = useCallback((updater: (p: AccountPlan) => AccountPlan) => {
    setPlanState(prev => {
      if (!prev) return prev;
      const next = updater(prev);
      savePlan(next);
      return next;
    });
  }, []);

  const days = useMemo<number | null>(() => plan ? daysSinceActivity(plan) : null, [plan]);

  // Stall detection — if no activity ever logged (days === null), don't flag as stalled
  const isStalled = plan && days !== null
    ? days >= (plan.disqualification.days_without_progress_soft ?? 14)
    : false;
  // (legacy isHardKill removed in D-090 — replaced by isScoreHardKill/isDaysHardKill
  //  below, which both require evidence before firing the score-based path.)

  // ── M-PACT scoring: Option 1 (Decision #5, qa-fix-f) ────────────────────
  // Derives selectedOption (0-4) from free-text answer length.
  // Compatible with computeCpScore in OperationalHelix which uses
  // parseInt(selectedOption) / (4 * count) * 100 for the composite score.
  // Allows cleared status (score >= 70%) for comprehensive answers (200+ chars).
  function deriveSelectedOptionFromFreeText(trimmed: string): number {
    if (trimmed.length === 0)   return 0;  // no answer
    if (trimmed.length < 20)   return 1;  // minimal (25%)
    if (trimmed.length < 80)   return 2;  // developing (50%)
    if (trimmed.length < 200)  return 3;  // substantive (75%)
    return 4;                              // comprehensive (100%) — enables cleared status
  }

  // ── M-PACT answer save ──
  function handleMpactSave(cpKey: CpKey, answer: string, notes: string) {
    if (!plan) return;
    const meta = CP_META.find(c => c.key === cpKey);
    const oldVal = plan.mpact[cpKey].answer;
    const trimmed = answer.trim();
    const selectedOption = deriveSelectedOptionFromFreeText(trimmed);
    setPlan(p => ({
      ...p,
      mpact: {
        ...p.mpact,
        [cpKey]: {
          ...p.mpact[cpKey],
          answered: trimmed.length > 0,
          answer,
          notes,
          points_earned: selectedOption,  // 0-4, feeds OperationalHelix computeCpScore
          updated_by: "ae",
          updated_at: new Date().toISOString(),
        },
      },
      last_activity_at: new Date().toISOString(),
      audit_log: [
        ...p.audit_log,
        {
          id: crypto.randomUUID(),
          field: `mpact.${cpKey}.answer`,
          old_value: oldVal,
          new_value: answer,
          changed_by: "ae",
          changed_at: new Date().toISOString(),
          session_note: notes,
        } as AuditEntry,
      ],
    }));
    setMpactModal(null);
  }

  // ── Contact add ──
  // Two writes:
  //   1. relationship_map (plan blob) — local cache, then account_plans upsert
  //      via setPlan -> savePlan (Supabase-backed in account-plan-store.ts).
  //   2. contacts table — first-class CRM contact row so it shows up in
  //      pipeline, M-PACT discovery, and any future contact-driven views.
  async function handleAddContact(node: RelationshipNode) {
    setPlan(p => ({
      ...p,
      bootcamp_seed: {
        ...p.bootcamp_seed,
        relationship_map: [...p.bootcamp_seed.relationship_map, node],
      },
      last_activity_at: new Date().toISOString(),
    }));
    setShowAddContact(false);

    try {
      const sb = supabase();
      const { data: authData } = await sb.auth.getUser();
      const uid = authData?.user?.id;
      if (!uid) return;

      const accountIdNum = parseInt(accountId, 10);
      const nameParts = (node.name || "").trim().split(/\s+/);
      const firstName = nameParts.slice(0, -1).join(" ") || nameParts[0] || "";
      const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";

      // contacts.email is NOT NULL — synthesize a placeholder when the
      // relationship-map node doesn't carry an email yet. UI can edit later.
      const rawEmail = (node as any).email as string | undefined;
      const handleSlug = (node.name || "contact")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ".")
        .replace(/(^\.|\.$)/g, "") || "contact";
      const fallbackEmail = `${handleSlug}+${Date.now()}@placeholder.invalid`;
      const finalEmail = rawEmail && rawEmail.trim() ? rawEmail.trim() : fallbackEmail;

      const session = getSession();
      const insertRow = {
        tenant_id: session?.tenantId ?? 1,
        account_id: Number.isFinite(accountIdNum) ? accountIdNum : null,
        created_by: uid,
        assigned_to: uid,
        first_name: firstName,
        last_name: lastName,
        company: accountName,
        title: node.role || null,
        email: finalEmail,
        linkedin_url: (node as any).linkedin ?? null,
        notes: node.notes || null,
      };
      const { error } = await sb.from("contacts").insert(insertRow);
      if (error) console.error("[AccountPlanTab] contact insert failed", error);
    } catch (err) {
      console.error("[AccountPlanTab] contact insert threw", err);
    }
  }

  // ── Opportunity update ──
  function handleOppUpdate(updated: Opportunity) {
    setPlan(p => ({
      ...p,
      opportunities: p.opportunities.map(o => o.id === updated.id ? updated : o),
      last_activity_at: new Date().toISOString(),
    }));
  }

  // ── New opportunity ──
  function handleNewOpp() {
    if (!plan) return;
    const opp = blankOpportunity(accountName);
    setPlan(p => ({
      ...p,
      opportunities: [...p.opportunities, opp],
    }));
    setNewOppId(opp.id);
  }

  // ── Bootcamp field save ──
  function saveBootcamp(field: keyof typeof plan.bootcamp_seed, val: string) {
    setPlan(p => ({
      ...p,
      bootcamp_seed: { ...p.bootcamp_seed, [field]: val },
      last_activity_at: new Date().toISOString(),
    }));
  }

  // ── Next play ──
  function handlePlayDone() {
    setPlan(p => ({
      ...p,
      next_best_play: {
        ...p.next_best_play,
        auto_ranked: false,
        description: "Marked done by ae — awaiting next play generation.",
        play_type: "discover",
      },
      last_activity_at: new Date().toISOString(),
    }));
  }

  if (loading || !plan) {
    return <div className="p-8 text-sm text-neutral-500">Loading plan...</div>;
  }

  const mpactRows = CP_META.map(meta => ({
    ...meta,
    field: plan.mpact[meta.key],
  }));

  const answeredCount = mpactRows.filter(r => r.field.answered).length;

  // D-090 (2026-05-22): Suppress score-based hard-kill on early accounts.
  // A brand-new Suspect at score 0 should not be flagged "consider disqualifying"
  // before the rep has even started discovery. We require at least 3 M-PACT
  // synthesis entries before the low-score path can trigger. The days-stalled
  // hard-kill path is unchanged — if an account sits untouched past the hard
  // limit, it still surfaces.
  const hasEnoughEvidence = answeredCount >= 3;
  const isScoreHardKill = hasEnoughEvidence && plan
    ? compositeScore < plan.disqualification.threshold_score
    : false;
  const isDaysHardKill = plan && days !== null
    ? days >= (plan.disqualification.days_without_progress_hard ?? 30)
    : false;
  const showHardKillBanner = isScoreHardKill || isDaysHardKill;

  const [, setLocation] = useLocation();

  return (
    <div className="space-y-0">
      {/* W21: Answer Questions CTA — canonical primary entry point for CP questions */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 0 14px",
          marginBottom: 4,
        }}
      >
        <div style={{ fontSize: 12, color: "#6B6B70" }}>
          Answer CP questions to improve the Composite score for this account.
        </div>
        <button
          type="button"
          onClick={() => setLocation(`/accounts/${accountId}/answer`)}
          style={{
            padding: "9px 18px",
            borderRadius: 7,
            fontSize: 13,
            fontWeight: 700,
            background: "#DC2626",
            color: "#FFFFFF",
            border: "none",
            cursor: "pointer",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
          title="Open the canonical Answer Lane for this account"
        >
          Answer Questions
        </button>
      </div>

      {/* ── 1. Header ── */}
      <Section title="Account overview" defaultOpen>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Account" value={accountName} />
          <StatCard label="Industry" value={industry || "—"} />
          <StatCard label="Composite score" value={compositeScore.toFixed(1)} valueColor={getHealthColor(compositeScore)} />
          <StatCard label="Days since last touch" value={days === null ? "—" : `${days}d`} valueColor={days === null ? "#6B6B70" : days > 14 ? RED : days > 7 ? "#EA580C" : "#16A34A"} />
        </div>
      </Section>

      {/* ── 7. Disqualification banner (D-090: only when warranted) ── */}
      {(isStalled || showHardKillBanner) && (
        <div
          className="rounded-xl px-5 py-4 mb-4 flex items-start gap-3"
          style={{
            background: showHardKillBanner ? "rgba(220,38,38,0.10)" : "rgba(234,88,12,0.10)",
            border: `1px solid ${showHardKillBanner ? "#DC2626" : "#EA580C"}`,
          }}
        >
          <AlertTriangle
            className="w-5 h-5 mt-0.5 flex-shrink-0"
            style={{ color: showHardKillBanner ? RED : "#EA580C" }}
          />
          <div>
            <div className="text-sm font-semibold" style={{ color: showHardKillBanner ? RED : "#0B0B0F" }}>
              {isDaysHardKill
                ? `This account has been stalled ${days} days — past the hard-kill limit. Disqualify or advance now.`
                : isScoreHardKill
                  ? "Score is below threshold after meaningful discovery. Consider disqualifying."
                  : days !== null
                    ? `This account has been stalled ${days} days. Review and advance or disqualify.`
                    : "This account has no recent activity. Review and advance or disqualify."}
            </div>
            <div className="text-xs mt-1" style={{ color: showHardKillBanner ? "#DC2626" : "#1F1F23" }}>
              {isScoreHardKill
                ? `Score: ${compositeScore.toFixed(1)} (threshold: ${plan.disqualification.threshold_score}) · ${answeredCount}/10 CPs synthesized${days !== null ? ` · Last touch ${days}d ago` : ""}`
                : isDaysHardKill
                  ? `Stalled ${days}d (hard-kill limit: ${plan.disqualification.days_without_progress_hard}d)`
                  : `Soft-kill limit is ${plan.disqualification.days_without_progress_soft}d. Hard-kill at ${plan.disqualification.days_without_progress_hard}d.`}
            </div>
          </div>
        </div>
      )}

      {/* ── 2. Bootcamp foundation ── */}
      <Section title="Bootcamp foundation" defaultOpen>
        <div className="space-y-4">
          <EditableText
            label="Why this account"
            value={plan.bootcamp_seed.why_this_account}
            onSave={v => saveBootcamp("why_this_account", v)}
            multiline
          />
          <EditableText
            label="Initial pain hypothesis"
            value={plan.bootcamp_seed.initial_pain_hypothesis}
            onSave={v => saveBootcamp("initial_pain_hypothesis", v)}
            multiline
          />
          <EditableText
            label="Source of awareness"
            value={plan.bootcamp_seed.source_of_awareness}
            onSave={v => saveBootcamp("source_of_awareness", v)}
          />
          <EditableText
            label="Competitive intel"
            value={plan.bootcamp_seed.competitive_intel}
            onSave={v => saveBootcamp("competitive_intel", v)}
            multiline
          />
          <EditableText
            label="First move"
            value={plan.bootcamp_seed.first_move}
            onSave={v => saveBootcamp("first_move", v)}
            multiline
          />
          <EditableText
            label="Target personas"
            value={plan.bootcamp_seed.target_personas}
            onSave={v => saveBootcamp("target_personas", v)}
          />
        </div>
      </Section>

      {/* ── 3. Relationship map ── */}
      <Section
        title="Relationship map"
        badge={
          <span className="text-[10px] font-medium px-2 py-0.5 rounded" style={{ background: "#FAFAF7", color: "#0B0B0F" }}>
            {plan.bootcamp_seed.relationship_map.length} contacts
          </span>
        }
        defaultOpen
      >
        {plan.bootcamp_seed.relationship_map.length === 0 ? (
          <p className="text-sm text-neutral-500 italic mb-3">No contacts mapped yet.</p>
        ) : (
          <div className="overflow-x-auto mb-3">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-100">
                  {["Name", "Role", "Owner type", "Warmth", "Last touch", "Notes"].map(h => (
                    <th key={h} className="text-left text-[10px] uppercase tracking-wider text-neutral-500 pb-2 pr-3 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {plan.bootcamp_seed.relationship_map.map((c, i) => (
                  <tr key={i} className="border-b border-neutral-50 hover:bg-neutral-50">
                    <td className="py-2 pr-3 font-medium whitespace-nowrap" style={{ color: BLACK }}>
                      <span className="inline-flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-neutral-300" />
                        {c.name}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-neutral-600 whitespace-nowrap">{c.role}</td>
                    <td className="py-2 pr-3">
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded"
                        style={{ background: (OWNER_COLOR[c.owner] || "#8E8E94") + "18", color: OWNER_COLOR[c.owner] || "#8E8E94" }}
                      >
                        {OWNER_LABELS[c.owner] || c.owner}
                      </span>
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap">{warmthStars(c.warmth)}</td>
                    <td className="py-2 pr-3 text-neutral-500 text-[11px] whitespace-nowrap">{c.last_touch ? relTime(c.last_touch) : "—"}</td>
                    <td className="py-2 text-neutral-500 text-[12px] max-w-[200px] truncate">{c.notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <button
          onClick={() => setShowAddContact(true)}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded text-xs font-medium border border-neutral-200 hover:bg-neutral-50 transition-colors"
          style={{ color: BLACK }}
        >
          <Plus className="w-3.5 h-3.5" /> Add contact
        </button>
      </Section>

      {/* ── 4. M-PACT discovery state ── */}
      <Section
        title="M-PACT discovery"
        badge={
          <div className="flex items-center gap-1.5">
            <span
              className="text-[10px] font-medium px-2 py-0.5 rounded"
              style={{
                background: answeredCount >= 8 ? "rgba(22,163,74,0.14)" : answeredCount >= 5 ? "rgba(234,179,8,0.16)" : "rgba(220,38,38,0.12)",
                color: answeredCount >= 8 ? "#16A34A" : answeredCount >= 5 ? "#0B0B0F" : "#DC2626",
              }}
              title="Checkpoints with a CP-level synthesis recorded in this Plan"
            >
              {answeredCount}/10 CPs synthesized
            </span>
            {helixAnsweredQs.total > 0 && (
              <span
                className="text-[10px] font-medium px-2 py-0.5 rounded"
                style={{ background: "#EFEFEE", color: "#1F1F23" }}
                title="Question-level answers captured in the Opportunity DNA tab"
              >
                {helixAnsweredQs.answered}/{helixAnsweredQs.total} questions answered
              </span>
            )}
          </div>
        }
        defaultOpen
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-100">
                {["CP", "Checkpoint", "Status", "Answer", "Updated by", "Updated at", ""].map(h => (
                  <th key={h} className="text-left text-[10px] uppercase tracking-wider text-neutral-500 pb-2 pr-3 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mpactRows.map(row => {
                const status = !row.field.answered
                  ? "unanswered"
                  : row.field.answer.length < 20
                  ? "partial"
                  : "answered";
                const statusStyle = {
                  unanswered: { bg: "#EFEFEE", color: "#8E8E94" },
                  partial:    { bg: "rgba(234,179,8,0.16)", color: "#0B0B0F" },
                  answered:   { bg: "rgba(22,163,74,0.14)", color: "#16A34A" },
                }[status];
                return (
                  <tr key={row.key} className="border-b border-neutral-50 hover:bg-neutral-50 group">
                    <td className="py-2.5 pr-2 text-[11px] font-semibold text-neutral-500 w-10">CP{row.cp}</td>
                    <td className="py-2.5 pr-3 font-medium whitespace-nowrap" style={{ color: BLACK }}>{row.label}</td>
                    <td className="py-2.5 pr-3">
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded whitespace-nowrap"
                        style={{ background: statusStyle.bg, color: statusStyle.color }}
                      >
                        {status}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3 max-w-[280px]">
                      {row.field.answer ? (
                        <TruncatedAnswer answer={row.field.answer} />
                      ) : (
                        <span className="text-neutral-300 italic text-xs">No answer yet</span>
                      )}
                    </td>
                    <td className="py-2.5 pr-3 text-[11px] text-neutral-500 whitespace-nowrap">{row.field.updated_by || "—"}</td>
                    <td className="py-2.5 pr-3 text-[11px] text-neutral-500 whitespace-nowrap">
                      {row.field.updated_at ? relTime(row.field.updated_at) : "—"}
                    </td>
                    <td className="py-2.5">
                      <button
                        onClick={() => setMpactModal(row.key)}
                        className="px-2.5 py-1 rounded text-[11px] font-medium opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ background: BLACK, color: "#fff" }}
                      >
                        Answer
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>

      {/* ── 5. Opportunities ── */}
      <Section
        title="Opportunities"
        badge={
          <span className="text-[10px] font-medium px-2 py-0.5 rounded" style={{ background: "#FAFAF7", color: "#0B0B0F" }}>
            {plan.opportunities.length}
          </span>
        }
        defaultOpen
      >
        <div className="mb-3">
          <button
            onClick={handleNewOpp}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded text-xs font-medium"
            style={{ background: BLACK, color: "#fff" }}
          >
            <Plus className="w-3.5 h-3.5" /> New opportunity
          </button>
        </div>
        {plan.opportunities.length === 0 ? (
          <p className="text-sm text-neutral-500 italic">No opportunities yet. Use the button above to spawn one.</p>
        ) : (
          plan.opportunities.map(opp => {
            const accountIdNum = parseInt(accountId, 10);
            return (
              <OpportunityPlanCard
                key={opp.id}
                opp={opp}
                mpact={plan.mpact}
                accountId={Number.isNaN(accountIdNum) ? 0 : accountIdNum}
                onUpdate={handleOppUpdate}
                isNew={opp.id === newOppId}
                onClose={() => setNewOppId(null)}
              />
            );
          })
        )}
      </Section>

      {/* ── 6. Next best play ── */}
      <Section title="Next best play" defaultOpen>
        <div
          className="rounded-lg p-4 flex items-start justify-between gap-4"
          style={{ background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.35)" }}
        >
          <div className="flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: "#16A34A" }}>
              {plan.next_best_play.play_type.toUpperCase()}
              {plan.next_best_play.auto_ranked && (
                <span className="ml-2 text-[9px] font-normal opacity-70">auto-ranked</span>
              )}
            </div>
            <p className="text-sm font-medium" style={{ color: BLACK }}>{plan.next_best_play.description}</p>
            {plan.next_best_play.revenue_impact_estimate > 0 && (
              <p className="text-xs mt-1" style={{ color: "#16A34A" }}>
                Revenue impact estimate:{" "}
                <strong>
                  {plan.next_best_play.revenue_impact_estimate >= 1000
                    ? `$${(plan.next_best_play.revenue_impact_estimate / 1000).toFixed(0)}K`
                    : `$${plan.next_best_play.revenue_impact_estimate}`}
                </strong>{" "}
                probability-weighted
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <button
              onClick={handlePlayDone}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium"
              style={{ background: "#16A34A", color: "#fff" }}
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Mark done
            </button>
            <button
              onClick={() => {
                const desc = window.prompt("Override play description:", plan.next_best_play.description);
                if (desc !== null) {
                  setPlan(p => ({
                    ...p,
                    next_best_play: { ...p.next_best_play, description: desc, auto_ranked: false },
                  }));
                }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium border border-neutral-200 hover:bg-neutral-50"
              style={{ color: BLACK }}
            >
              <RefreshCw className="w-3.5 h-3.5" /> Override
            </button>
          </div>
        </div>
      </Section>

      {/* ── 8. Audit log ── */}
      <Section title="Audit log" defaultOpen={false}>
        {plan.audit_log.length === 0 ? (
          <p className="text-sm text-neutral-500 italic">No changes recorded yet.</p>
        ) : (
          <div className="space-y-1.5">
            {[...plan.audit_log].reverse().slice(0, 20).map(entry => (
              <div
                key={entry.id}
                className="flex items-start gap-3 text-xs py-2 border-b border-neutral-50"
              >
                <span className="text-neutral-500 whitespace-nowrap shrink-0">
                  {entry.changed_at ? new Date(entry.changed_at).toLocaleDateString() : "—"}
                </span>
                <span className="text-neutral-500 shrink-0">{entry.changed_by || "ae"}</span>
                <span className="font-mono text-[10px] text-neutral-500 shrink-0">{entry.field}</span>
                <span className="text-neutral-700 truncate">{entry.new_value}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* ── Modals ── */}
      {mpactModal && (() => {
        const meta = CP_META.find(m => m.key === mpactModal);
        if (!meta) return null;
        return (
          <MpactAnswerModal
            cp={meta.cp}
            question={meta.question}
            label={meta.label}
            field={plan.mpact[mpactModal]}
            onSave={(answer, notes) => handleMpactSave(mpactModal, answer, notes)}
            onClose={() => setMpactModal(null)}
          />
        );
      })()}

      {showAddContact && (
        <AddContactModal
          onAdd={handleAddContact}
          onClose={() => setShowAddContact(false)}
        />
      )}
    </div>
  );
}

// ── Small helper components ────────────────────────────────────────────────────

function StatCard({ label, value, valueColor }: { label: string; value: string; valueColor?: string }) {
  return (
    <div className="rounded-lg p-3" style={{ background: BG, border: "1px solid #E5E5E7" }}>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="text-sm font-semibold mt-1 truncate" style={{ color: valueColor || BLACK }}>{value}</div>
    </div>
  );
}

function TruncatedAnswer({ answer }: { answer: string }) {
  const [expanded, setExpanded] = useState(false);
  const short = answer.length > 80;
  return (
    <span className="text-neutral-700">
      {expanded || !short ? answer : answer.slice(0, 80) + "…"}
      {short && (
        <button
          onClick={() => setExpanded(e => !e)}
          className="ml-1 text-neutral-500 text-[11px] hover:underline"
        >
          {expanded ? "less" : "more"}
        </button>
      )}
    </span>
  );
}
